import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, FileCheck2Icon, XIcon } from 'lucide-react'
import { Button, DialogDescription, DialogTitle, IconButton } from '@rentflowdev/ui'

export type PropertySavedFlashVariant = 'success' | 'draft' | 'error'

interface PropertySavedFlashProps {
	variant: PropertySavedFlashVariant
	propertyTitle: string
	errorDetail?: string | null
	onAction: () => void
	onClose: () => void
}

export function PropertySavedFlash({
	variant,
	propertyTitle,
	errorDetail,
	onAction,
	onClose,
}: PropertySavedFlashProps) {
	const { t } = useTranslation()
	const isSuccess = variant === 'success'
	const isError = variant === 'error'

	const title = t(`properties.wizard.saved.${variant}`)
	const description = t(`properties.wizard.saved.${variant}Description`, { title: propertyTitle })
	const actionLabel = isError
		? t('properties.wizard.saved.closeBtn')
		: isSuccess
			? t('properties.wizard.saved.publishBtn')
			: t('properties.wizard.saved.okBtn')

	return (
		<div className="relative flex items-center justify-center px-10 py-12">
			<IconButton
				variant="ghost"
				size="sm"
				type="button"
				onClick={onClose}
				aria-label={t('common.close')}
				className="absolute top-4 right-4"
			>
				<XIcon />
			</IconButton>

			<div className="flex w-[29.875rem] flex-col items-center gap-5">
				<div
					className={
						isError
							? 'flex size-[9.375rem] items-center justify-center rounded-full bg-(--color-destructive)/10'
							: isSuccess
								? 'flex size-[9.375rem] items-center justify-center rounded-full bg-(--color-brand-light)'
								: 'flex size-[9.375rem] items-center justify-center rounded-full bg-(--color-brand-light) opacity-50'
					}
				>
					{isError
						? <AlertCircleIcon className="size-16 text-(--color-destructive)" strokeWidth={1.5} />
						: <FileCheck2Icon className="size-16 text-(--color-brand)" strokeWidth={1.5} />
					}
				</div>

				<div className="flex flex-col items-center gap-3 text-center w-full">
					<DialogTitle className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground)">
						{title}
					</DialogTitle>
					<DialogDescription className="text-base leading-6 text-(--color-muted-foreground)">
						{description}
					</DialogDescription>
					{errorDetail && (
						<p className="mt-1 rounded bg-(--color-destructive)/10 px-3 py-2 text-xs font-mono text-(--color-destructive) break-all text-left w-full">
							{errorDetail}
						</p>
					)}
				</div>

				<Button
					variant={isError ? 'secondary' : 'default'}
					onClick={onAction}
				>
					{actionLabel}
				</Button>
			</div>
		</div>
	)
}
